import React from 'react';
import { formattedPrice } from '../utils/priceCalculations';
import '../css/components/PriceChangeNotice.css';

/**
 * Dirección del cambio de precio: 'down', 'up' o null si no se puede saber.
 *
 * `info` es el objeto price_change_info. Puede venir de dos fuentes con forma
 * distinta:
 *  - No logueado (useCartPriceRevalidation): { old_price, new_price, message }
 *    donde old_price/new_price YA son los precios efectivos (con oferta).
 *  - Logueado (backend): trae old_effective_price/new_effective_price (los que
 *    paga el cliente) además de old_price/new_price (los regulares) y, desde
 *    task-1843, `direction`.
 * Se prefiere el precio EFECTIVO y se cae al regular si no viene.
 */
export function priceChangeDirection(info) {
  if (!info) return null;

  const oldP = Number(info.old_effective_price ?? info.old_price);
  const newP = Number(info.new_effective_price ?? info.new_price);
  if (Number.isFinite(oldP) && Number.isFinite(newP) && oldP > 0 && newP > 0) {
    if (newP < oldP) return 'down';
    if (newP > oldP) return 'up';
    return null;
  }

  if (info.direction === 'down' || info.direction === 'up') return info.direction;
  if (/disminu|baj/i.test(info.message || '')) return 'down';
  if (/aument|subi/i.test(info.message || '')) return 'up';
  return null;
}

/**
 * Aviso de cambio de precio en el sidecart, el carrito y el checkout/pago.
 *
 * Sólo aparece cuando el precio BAJA (task-1843): "El precio bajó a $X (antes $Y)".
 * Si sube, el carrito igual toma el precio nuevo, pero no se muestra aviso.
 */
export default function PriceChangeNotice({ info }) {
  if (priceChangeDirection(info) !== 'down') return null;

  const oldP = Number(info.old_effective_price ?? info.old_price);
  const newP = Number(info.new_effective_price ?? info.new_price);
  const hasNums = Number.isFinite(oldP) && Number.isFinite(newP) && oldP > 0 && newP > 0;

  return (
    <div className="priceChangeNotice priceChangeNotice--down">
      <i className="fa-solid fa-arrow-trend-down"></i>
      <span>
        El precio bajó
        {hasNums &&
          ` a $${formattedPrice(Math.round(newP))} (antes $${formattedPrice(Math.round(oldP))})`}
      </span>
    </div>
  );
}
